import { useEffect, useState } from 'react'
import { useApi } from '../../../api/api'
import { useDropzone } from 'react-dropzone'
import { useDispatch, useSelector } from 'react-redux'
import { UploadIcon, FileIcon } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import {
    ScoopTextField,
    ScoopButton,
    ScoopDialog,
    ScoopSpinner,
    ScoopColumn,
    useToast,
} from '../../ui'
import { EVENTS, trackEvent } from '../../../events'
import { useCanvases } from '../../../api/hooks/canvases/useCanvases'
import styles from './UploadPPT.module.scss'

// when user uploads an image to S3, make sure the filename is safe
function safeS3FileName(str) {
    return str.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/ /g, '_')
}

export default function UploadPPT({ isOpen, onClose, canvasName }) {
    const toast = useToast()
    const { acceptedFiles, getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
        useDropzone({
            accept: {
                'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
                    '.pptx',
                ],
            },
        })

    // Hook to fetch data
    const { postData: postDataDefault } = useApi()
    const { postData: postDataWorkspaceactions } = useApi(
        'https://pig8gecvvk.execute-api.us-west-2.amazonaws.com/corsair/workspaceactions'
    )
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const userID = useSelector((state) => state.auth.userID)
    const { refetch } = useCanvases()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [label, setLabel] = useState('')
    const [description, setDescription] = useState('')
    const [step, setStep] = useState(0) // 0 for file upload, 1 for additional details

    const acceptedFileItems = acceptedFiles.map((file) => (
        <li key={file.path}>
            {file.path} - {file.size} bytes
        </li>
    ))

    // Build dropzone className based on state
    const dropzoneClassName = [
        styles.dropzone,
        isFocused && styles.focused,
        isDragAccept && styles.accept,
        isDragReject && styles.reject,
    ]
        .filter(Boolean)
        .join(' ')

    const handleNext = () => {
        setStep(1)
    }

    const handleBack = () => {
        setStep(0)
    }

    useEffect(() => {
        if (acceptedFiles.length > 0 && label === '') {
            setLabel(acceptedFiles[0].name.replace('.pptx', ''))
        }
    }, [acceptedFiles, label])

    const handleUpload = async () => {
        setIsLoading(true)
        let payload = {
            action: 'uploadPPT',
            workspaceID: workspaceID,
            canvasName: canvasName,
            isDev: process.env.REACT_APP_SCOOP_ENV === 'dev',
        }
        try {
            const presignedData = await postDataWorkspaceactions({
                ...payload,
                fileName: safeS3FileName(acceptedFiles[0].name),
                fileType: acceptedFiles[0].type,
            })
            await fetch(presignedData.signedRequest, {
                method: 'PUT',
                body: acceptedFiles[0],
                headers: {
                    'Content-Type': acceptedFiles[0].type,
                },
            })
            toast.success(
                'PowerPoint file uploaded successfully! Please wait a few minutes for the new canvas to be created.'
            )
            await createCanvasFromPresentation()
        } catch (error) {
            toast.error('Error uploading file')
            setTimeout(() => {
                onClose()
            }, 3000)
        }
    }

    const createCanvasFromPresentation = async () => {
        const action = {
            action: 'createCanvasFromPresentation',
            workspaceID: workspaceID,
            userID: userID,
            label: label,
            description: description,
            fileName: safeS3FileName(acceptedFiles[0].name),
        }
        try {
            const result = await postDataDefault(action)
            if (result?.canvasID) {
                dispatch({
                    type: 'APPLY_LOADED_OBJECTS_STATE',
                    payload: [],
                })
                refetch()
                navigate(result.canvasID, { state: 'newCanvas' })
                trackEvent(EVENTS.CANVAS.CREATE_FROM_PRESENTATION_COMPLETE, {
                    canvas: { canvasID: `${result.canvasID}` },
                    presentation: { presentation: `${result.presentationID}` },
                })
                try {
                    // userflow.track('ppt_uploaded', {
                    //     status: 'success',
                    //     file: acceptedFiles[0].name,
                    // })
                } catch (error) {}
                // GJ Jan 23: Commented out because UserFlow is supposed to take over the onboarding checklist
                // if (onboardingChecklist === '2') dispatch(setOnboardingChecklist('3'))
            }
            setIsLoading(false)
        } catch (error) {
            console.error('Error creating canvas from presentation:', error)
            try {
                // userflow.track('ppt_uploaded', {
                //     status: 'error',
                //     file: acceptedFiles[0].name,
                // })
            } catch (error) {}
        }
    }

    const dialogActions = (
        <div className={styles.dialogActions}>
            {step === 0 && (
                <>
                    <ScoopButton variant="text" onClick={onClose}>
                        Cancel
                    </ScoopButton>
                    <ScoopButton
                        variant="contained"
                        onClick={handleNext}
                        disabled={acceptedFiles.length === 0}
                    >
                        Next
                    </ScoopButton>
                </>
            )}
            {step === 1 && (
                <>
                    <ScoopButton variant="text" onClick={handleBack} disabled={isLoading}>
                        Back
                    </ScoopButton>
                    <ScoopButton variant="contained" onClick={handleUpload} disabled={isLoading}>
                        {isLoading ? <ScoopSpinner /> : 'Create'}
                    </ScoopButton>
                </>
            )}
        </div>
    )

    return (
        <>
            <ScoopDialog
                open={isOpen}
                onClose={onClose}
                title={step === 0 ? 'Upload a PowerPoint file' : 'Create canvas'}
                actions={dialogActions}
                maxWidth="sm"
                style={{ minWidth: '500px' }}
            >
                {step === 0 && (
                    <section className={styles.section}>
                        <div {...getRootProps({ className: dropzoneClassName })}>
                            <input {...getInputProps()} />
                            <UploadIcon size={48} weight="regular" className={styles.uploadIcon} />
                            <div className={styles.uploadLabel}>PPTX</div>
                            <div className={styles.uploadText}>
                                Drag 'n' drop your data file here or{' '}
                            </div>
                            <span className={styles.uploadLink}>click here to select</span>
                        </div>
                        {acceptedFiles.length > 0 && (
                            <ul className={styles.fileList}>{acceptedFileItems}</ul>
                        )}
                    </section>
                )}

                {step === 1 && (
                    <section className={styles.section}>
                        <ScoopColumn>
                            {acceptedFiles.map((file) => (
                                <div className={styles.fileContainer} key={file.path}>
                                    <div className={styles.fileHeader}>
                                        <FileIcon
                                            size={20}
                                            weight="regular"
                                            className={styles.fileIcon}
                                        />
                                        <div className={styles.fileName}>{file.path}</div>
                                    </div>
                                    <div className={styles.inputContainer}>
                                        <ScoopTextField
                                            placeholder="Presentation name"
                                            value={label}
                                            onChange={(event) => setLabel(event.target.value)}
                                            fullWidth
                                        />
                                    </div>
                                    <div className={styles.inputContainer}>
                                        <ScoopTextField
                                            placeholder="Presentation description"
                                            value={description}
                                            onChange={(event) => setDescription(event.target.value)}
                                            fullWidth
                                        />
                                    </div>
                                </div>
                            ))}
                        </ScoopColumn>
                    </section>
                )}
            </ScoopDialog>
        </>
    )
}
